import { useEffect, useState } from "react";
import { Loader2, Check, X as XIcon, EyeOff, ExternalLink } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

const TABS = [
    { id: "pending",  label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "hidden",   label: "Hidden" },
];

export default function AdminReels() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/reels", { params: { status: tab } }).then((r) => r.data?.items || []).catch(() => []),
            api.get("/admin/reels/reports").then((r) => r.data?.items || []).catch(() => []),
        ]).then(([reels, reps]) => { setItems(reels); setReports(reps); })
          .finally(() => setLoading(false));
    };

    useEffect(load, [tab]);

    const moderate = async (id, status) => {
        try {
            await api.patch(`/admin/reels/${id}`, { status });
            toast.success(`Reel ${status}`);
            load();
        } catch (e) {
            toast.error("Could not update reel");
        }
    };

    const reportCount = (rid) => reports.filter((r) => r.reel_id === rid && r.status === "open").length;

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-24" data-testid="admin-reels-page">
            <MobileScreenHeader title="Reels Moderation" subtitle={`${reports.length} total reports`} testid="admin-reels-header" />
            <div className="px-4 mt-3 flex gap-1.5 overflow-x-auto no-scrollbar">
                {TABS.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`admin-reels-tab-${t.id}`}
                        className="px-3 h-8 rounded-full text-[11.5px] font-extrabold whitespace-nowrap"
                        style={{
                            background: tab === t.id ? "linear-gradient(90deg, #DAA520, #F59E0B)" : "white",
                            color: tab === t.id ? "#001a3d" : "#002657",
                            border: "1px solid rgba(0,38,87,0.1)",
                        }}>
                        {t.label}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#002657" }} /></div>
            ) : items.length === 0 ? (
                <div className="px-4 mt-6 text-center text-[12px]" style={{ color: "rgba(0,38,87,0.6)" }}>
                    No reels in this bucket.
                </div>
            ) : (
                <div className="px-4 mt-3 space-y-3">
                    {items.map((r) => {
                        const rc = reportCount(r.id);
                        return (
                            <div key={r.id} className="rounded-2xl bg-white p-3 border shadow-sm"
                                 style={{ borderColor: "rgba(0,38,87,0.08)" }}
                                 data-testid={`admin-reel-${r.id}`}>
                                <div className="flex gap-3">
                                    <div className="w-16 h-24 rounded-lg overflow-hidden bg-black shrink-0">
                                        {r.thumbnail_url ? (
                                            <img src={fileUrl(r.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <video src={fileUrl(r.video_url)} muted playsInline className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <div className="font-heading font-extrabold text-[12.5px] truncate" style={{ color: "#002657" }}>
                                                {r.property?.title || "Property"}
                                            </div>
                                            {rc > 0 && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-extrabold"
                                                      style={{ background: "#FEE2E2", color: "#B91C1C" }}>
                                                    {rc} reports
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10.5px]" style={{ color: "rgba(0,38,87,0.65)" }}>
                                            {r.creator?.name || "—"} · {r.city || "—"}
                                        </div>
                                        <div className="text-[10.5px] mt-1 line-clamp-2" style={{ color: "rgba(0,38,87,0.85)" }}>
                                            {r.caption || <em>No caption</em>}
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <a href={`/reels/${r.id}`} target="_blank" rel="noreferrer"
                                               data-testid={`admin-reel-open-${r.id}`}
                                               className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10.5px] font-bold border"
                                               style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                                <ExternalLink className="w-3 h-3" /> Open
                                            </a>
                                            {r.property?.id && (
                                                <a href={`/properties/${r.property.id}`} target="_blank" rel="noreferrer"
                                                   className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10.5px] font-bold border"
                                                   style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                                    Property
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-1.5">
                                    <button onClick={() => moderate(r.id, "approved")}
                                        data-testid={`admin-reel-approve-${r.id}`}
                                        className="inline-flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-extrabold text-white"
                                        style={{ background: "#059669" }}>
                                        <Check className="w-3.5 h-3.5" /> Approve
                                    </button>
                                    <button onClick={() => moderate(r.id, "rejected")}
                                        data-testid={`admin-reel-reject-${r.id}`}
                                        className="inline-flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-extrabold text-white"
                                        style={{ background: "#DC2626" }}>
                                        <XIcon className="w-3.5 h-3.5" /> Reject
                                    </button>
                                    <button onClick={() => moderate(r.id, "hidden")}
                                        data-testid={`admin-reel-hide-${r.id}`}
                                        className="inline-flex items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-extrabold"
                                        style={{ background: "#F3F4F6", color: "#111827" }}>
                                        <EyeOff className="w-3.5 h-3.5" /> Hide
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
